// The patient's Terrain readings, from reads that already exist:
//   labs   GET /api/Patients/{id}/LabObservations?loinc=   (one per catalog LOINC; history, oldest first)
//   vitals GET /api/Patients/GetVitalsHistoryByCode?PatientId=&Code=   (systolic, diastolic, BMI, weight)
// Nothing is computed that the record does not hold: a non-numeric, cancelled or entered-in-error result is skipped.
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { LabObservationTrend } from '@/types/lab-results-api';
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';
import { TERRAIN_MARKERS } from '../lib/terrain-catalog';
import { markerKeyForTestName } from '../lib/terrain-names';
import { useLabResultGroups } from '@/patients/components/chart/labs/use-lab-result-groups';
import type { LabResultGroup } from '@/patients/lib/lab-results';
import type { TerrainReading, TerrainReadings } from '../lib/terrain-model';

const NUMERIC = /^-?\d+(\.\d+)?$/;

/**
 * Results with no catalog LOINC, matched to a marker by test name (terrain-names.ts). Results whose LOINC is in the catalog
 * are skipped here: they are read by code above, so nothing is counted twice. Non-numeric values and undated groups are skipped.
 */
export function namedReadings(groups: readonly LabResultGroup[], catalogLoincs: ReadonlySet<string>): TerrainReadings {
  const out: Record<string, TerrainReading[]> = {};
  for (const g of groups) {
    if (!g.date) continue;
    for (const row of [...g.tests.flatMap((t) => t.analytes), ...g.otherAnalytes]) {
      if (row.trendKey?.loinc && catalogLoincs.has(row.trendKey.loinc)) continue;
      const key = markerKeyForTestName(row.display);
      const v = row.value?.trim() ?? '';
      if (!key || !NUMERIC.test(v)) continue;
      (out[key] ??= []).push({ value: Number(v), unit: row.unit ?? '', date: g.date, refLow: row.refLow, refHigh: row.refHigh });
    }
  }
  return out;
}

const LAB_LOINCS = Array.from(new Set(TERRAIN_MARKERS.filter((m) => m.kind === 'lab').flatMap((m) => m.loincs)));
const LAB_LOINC_SET: ReadonlySet<string> = new Set(LAB_LOINCS);
const VITAL_CODE_LIST = [VITAL_CODES.SYSTOLIC, VITAL_CODES.DIASTOLIC, VITAL_CODES.BMI, VITAL_CODES.WEIGHT];
/** A systolic and a diastolic saved this close together are one blood pressure. */
export const BP_PAIR_MS = 5 * 60_000;
const SKIPPED_STATUS = /cancel|error|entered-in-error/i;

export interface TerrainReadingsResult {
  readings: TerrainReadings;
  isLoading: boolean;
  /** At least one read failed: what loaded is still shown, and the view says some results could not be read. */
  isPartial: boolean;
}

export function labPointsToReadings(trend: LabObservationTrend | undefined): TerrainReading[] {
  return (trend?.observations ?? []).flatMap((o) => {
    if (o.valueNumeric == null || !o.effectiveAt || (o.status && SKIPPED_STATUS.test(o.status))) return [];
    return [{ value: o.valueNumeric, unit: o.unitText || o.unit || '', date: o.effectiveAt, refLow: o.refLow, refHigh: o.refHigh }];
  });
}

function vitalNumber(v: PatientVital): number | null {
  const n = Number.parseFloat(v.quantity?.value ?? '');
  return Number.isFinite(n) ? n : null;
}

export function vitalsToReadings(byCode: Partial<Record<string, PatientVital[]>>): TerrainReadings {
  const simple = (code: string): TerrainReading[] =>
    (byCode[code] ?? []).flatMap((v) => {
      const n = vitalNumber(v);
      return n == null || !v.effectiveDateTime ? [] : [{ value: n, unit: v.quantity?.unit ?? '', date: v.effectiveDateTime }];
    });
  // Blood pressure: a systolic and the diastolic recorded with it (within BP_PAIR_MS, the closest one) make one reading.
  // A lone half is not shown.
  const diastolics = simple(VITAL_CODES.DIASTOLIC).map((d) => ({ ...d, t: Date.parse(d.date) }));
  const bp = simple(VITAL_CODES.SYSTOLIC).flatMap((s) => {
    const t = Date.parse(s.date);
    let best: (typeof diastolics)[number] | null = null;
    for (const d of diastolics) {
      const gap = Math.abs(d.t - t);
      if (Number.isFinite(gap) && gap <= BP_PAIR_MS && (!best || gap < Math.abs(best.t - t))) best = d;
    }
    if (!best) return [];
    // One diastolic belongs to one blood pressure.
    diastolics.splice(diastolics.indexOf(best), 1);
    return [{ value: s.value, value2: best.value, unit: 'mmHg', date: s.date }];
  });
  return { bp, bmi: simple(VITAL_CODES.BMI), weight: simple(VITAL_CODES.WEIGHT) };
}

export function useTerrainReadings(patientId: string | null | undefined): TerrainReadingsResult {
  const enabled = !!patientId;
  const id = encodeURIComponent(patientId ?? '');
  const labs = useQueries({
    queries: LAB_LOINCS.map((loinc) => ({
      // The same key and read as the chart's lab trend (use-patient-lab-results.ts), so both share the cache.
      queryKey: ['lab-observation-trend', patientId, `loinc=${encodeURIComponent(loinc)}`] as const,
      enabled,
      staleTime: 5 * 60_000,
      // One retry, not three: a failing read must not hold the whole view on "Reading the record…".
      retry: 1,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        get<LabObservationTrend>(`/api/Patients/${id}/LabObservations?loinc=${encodeURIComponent(loinc)}`, { signal }),
    })),
  });
  const vitals = useQueries({
    queries: VITAL_CODE_LIST.map((code) => ({
      queryKey: ['vitals-history', patientId, code] as const,
      enabled,
      staleTime: 5 * 60_000,
      // One retry, not three: a failing read must not hold the whole view on "Reading the record…".
      retry: 1,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        get<PatientVital[]>(`/api/Patients/GetVitalsHistoryByCode?PatientId=${id}&Code=${encodeURIComponent(code)}`, { signal }),
    })),
  });

  // Every result the Labs tab lists (lab-request draws and outside reports), for the ones with no catalog LOINC.
  const groups = useLabResultGroups(patientId);
  const allGroups = useMemo(() => [...groups.groups, ...groups.externalGroups], [groups.groups, groups.externalGroups]);
  const labData = labs.map((q) => q.data);
  const vitalData = vitals.map((q) => q.data);
  const readings = useMemo(() => {
    const out: TerrainReadings = {};
    const byName = namedReadings(allGroups, LAB_LOINC_SET);
    for (const m of TERRAIN_MARKERS) {
      if (m.kind !== 'lab') continue;
      out[m.key] = [...m.loincs.flatMap((l) => labPointsToReadings(labData[LAB_LOINCS.indexOf(l)])), ...(byName[m.key] ?? [])];
    }
    const byCode: Partial<Record<string, PatientVital[]>> = {};
    VITAL_CODE_LIST.forEach((code, i) => {
      byCode[code] = Array.isArray(vitalData[i]) ? vitalData[i] : [];
    });
    return { ...out, ...vitalsToReadings(byCode) };
    // The query results are new arrays every render; their data references are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...labData, ...vitalData, allGroups]);

  return {
    readings,
    // The view waits only until the FIRST reads answer; what is still loading fills in as it arrives (a slow or failing read
    // used to keep the whole view, radar included, on "Reading the record…").
    isLoading: enabled && [...labs, ...vitals].every((q) => q.isLoading),
    isPartial: [...labs, ...vitals].some((q) => q.isError) || groups.failed.length > 0,
  };
}
